import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core'
import { Location } from '@angular/common'
import { Router } from '@angular/router'
import { CaStateService } from './ca-state.service'
import { Store } from '@ngrx/store'
import { MainViewActions } from '../../core/layout/state/action-types'
import { Subscription } from 'rxjs'
import { selectBreadcrumbs } from '../../core/layout/state/main-view.selectors'
import { Title } from '@angular/platform-browser'
import { BreadcrumbProperties, BreadcrumbPropertyKey } from './breadcrumb-properties'

@Injectable({
    providedIn: 'root'
})
export class BreadcrumbsService implements OnDestroy {

    breadcrumbs: any[]
    #selectBreadcrumbs: Subscription
    // Cache for breadcrumb properties keyed by URL - survives navigation
    #propertiesCache: Map<string, BreadcrumbProperties> = new Map()

    constructor(@Inject(Store) private store: Store,
                @Inject(CaStateService) private caStateService: CaStateService,
                @Inject(Title) private titleService: Title,
                @Inject(NgZone) protected ngZone: NgZone,
                @Inject(Location) private location: Location,
                @Inject(Router) private router: Router,
    ) {
        this.#selectBreadcrumbs = this.store.select(selectBreadcrumbs).subscribe(data => {
            this.breadcrumbs = data
        })
    }

    ngOnDestroy(): void {
        this.#selectBreadcrumbs?.unsubscribe()
    }

    getBreadcrumbs(): any {
        return this.breadcrumbs
    }

    setBreadcrumbs(breadcrumbs): void {
        this.ngZone.run(() => {
            this.store.dispatch(MainViewActions.setBreadcrumbs({
                breadcrumbs: breadcrumbs
            }))
        })
    }

    deleteAllBreadcrumbs(): void {
        this.ngZone.run(() => {
            this.store.dispatch(MainViewActions.resetBreadcrumbs())
        })
        // Clear properties cache when breadcrumbs are reset (e.g., navigating to landing page)
        this.#propertiesCache.clear()
    }

    /**
     * Get cached properties for a specific URL
     * Used when building breadcrumbs to restore properties from previous navigation
     */
    getCachedPropertiesForUrl(url: string): BreadcrumbProperties {
        return this.#propertiesCache.get(url) || {}
    }

    /**
     * Clear the properties cache
     * Called when navigating to a list page (resetBreadcrumbs) to ensure
     * tab selection doesn't persist across different navigation contexts
     */
    clearPropertiesCache(): void {
        this.#propertiesCache.clear()
    }

    deleteLastBreadcrumb(): void {
        let breadcrumbs = this.getBreadcrumbs()
        breadcrumbs.splice(-1, 1)
        this.setBreadcrumbs(breadcrumbs)
    }

    isPreviousStepPreviewType(): boolean {
        let breadcrumbsHistory = this.getBreadcrumbs()

        if (breadcrumbsHistory.length > 1) {
            var state = breadcrumbsHistory[breadcrumbsHistory.length - 2].state
            return state.indexOf('.show') > -1
        } else {
            return false
        }
    }

    getCurrentBreadcrumbProperty<K extends BreadcrumbPropertyKey>(key: K): BreadcrumbProperties[K] | null {
        const currentUrl = this.router.url

        // First try to get from properties cache for current URL
        // This ensures we get properties for THIS page, not from previous navigation
        const cachedProperties = this.#propertiesCache.get(currentUrl)
        if (cachedProperties?.[key] !== undefined) {
            return cachedProperties[key]
        }

        // Fallback: check if current breadcrumb URL matches current router URL
        const breadcrumbsHistory = this.getBreadcrumbs()
        if (breadcrumbsHistory?.length > 0) {
            const lastBreadcrumb = breadcrumbsHistory[breadcrumbsHistory.length - 1]
            // Only use breadcrumb properties if it's for the same URL (same page)
            if (lastBreadcrumb.url === currentUrl && lastBreadcrumb.properties?.[key] !== undefined) {
                return lastBreadcrumb.properties[key]
            }
        }

        return null
    }

    setCurrentBreadcrumbProperty<K extends BreadcrumbPropertyKey>(key: K, value: BreadcrumbProperties[K]): void {
        const breadcrumbsHistory = this.getBreadcrumbs()

        if (breadcrumbsHistory?.length > 0) {
            breadcrumbsHistory[breadcrumbsHistory.length - 1].properties[key] = value
        }

        this.setBreadcrumbs(breadcrumbsHistory)

        // Also store in cache (survives navigation)
        const currentUrl = this.router.url
        if (!this.#propertiesCache.has(currentUrl)) {
            this.#propertiesCache.set(currentUrl, {})
        }
        this.#propertiesCache.get(currentUrl)[key] = value
    }

    /**
     * Set a property in the cache for a specific URL (before navigating to it)
     * Useful for pre-setting tab selection when navigating from view to edit
     */
    setPropertyForUrl<K extends BreadcrumbPropertyKey>(url: string, key: K, value: BreadcrumbProperties[K]): void {
        if (!this.#propertiesCache.has(url)) {
            this.#propertiesCache.set(url, {})
        }
        this.#propertiesCache.get(url)[key] = value
    }

    setCurrentBreadcrumbTitle(title): void {
        let breadcrumbsHistory = this.getBreadcrumbs()

        if (breadcrumbsHistory.length > 0) {
            breadcrumbsHistory[breadcrumbsHistory.length - 1].title = title
        }

        this.setBreadcrumbs(breadcrumbsHistory)
        this.titleService.setTitle(`${title} - CleverAssets`)
    }

    stepBackBreadcrumb(fallback?: () => void): void {
        let breadcrumbs = this.getBreadcrumbs()
        if (breadcrumbs.length > 1) {
            // Navigate back in browser history
            this.location.back()
        } else if (fallback) {
            fallback()
        } else {
            this.caStateService.setLandingPageState()
        }
    }
}
